import { useCallback, useContext, useMemo } from 'react'
import { useAuth } from '../../context/useAuth'
import { ChatContext } from '../../context/chatContextCore'
import { useWorkspace } from '../../context/useWorkspace'
import { useChatControls } from '../../hooks/useChatControls'

/**
 * Compose-chat entry points targeted at a specific workspace member.
 *
 * Two flows expose the same target from the member profile popup:
 *
 * - `pingInWorkspaceChat` — writes `@<displayName> ` into the workspace chat
 *   composer draft (via the same `tl:persist:v1:<uid>:<workspaceId>:chat.textInput`
 *   key TeamGroupChat reads on mount), then dispatches the existing
 *   `timelining:notification-open-chat` event so App-level routing switches
 *   the layout view to CHAT. Cheap and uses only what already exists.
 *
 * - `openPrivateChat` — locates a 2-member private channel whose `roles` map
 *   contains exactly the current user + target user; if none exists creates
 *   one with `createChannel({ type: 'private', ... })` and sets it as the
 *   active channel. Then dispatches the same open-chat event.
 */
export const useChatEntryForMember = () => {
  const { currentUser } = useAuth()
  const { activeWorkspaceId } = useWorkspace()
  const chat = useContext(ChatContext)
  const channelsList = useMemo(() => chat?.channelsList ?? [], [chat?.channelsList])
  const createChannel = chat?.createChannel
  const setActiveChannelId = chat?.setActiveChannelId
  const { canCreateDm } = useChatControls({ workspaceId: activeWorkspaceId })

  const composerDraftKey = useCallback(
    () => (currentUser?.uid && activeWorkspaceId
      ? `tl:persist:v1:${currentUser.uid}:${activeWorkspaceId}:chat.textInput`
      : null),
    [activeWorkspaceId, currentUser?.uid],
  )

  const openChatSurface = useCallback(() => {
    if (typeof window === 'undefined') return
    // App.tsx unwraps `detail.notification` — match that shape.
    window.dispatchEvent(new CustomEvent('timelining:notification-open-chat', {
      detail: { notification: { workspaceId: activeWorkspaceId } },
    }))
  }, [activeWorkspaceId])

  const pingInWorkspaceChat = useCallback((displayName: string) => {
    const key = composerDraftKey()
    if (key && typeof window !== 'undefined') {
      try {
        // usePersistedState wraps the raw value in JSON. Match that shape so
        // the composer initializes with the mention as its typed text.
        window.localStorage.setItem(key, JSON.stringify(`@${displayName} `))
      } catch {
        // storage may be blocked (Safari private mode); the caller still
        // gets navigation — the composer will just start empty.
      }
    }
    openChatSurface()
  }, [composerDraftKey, openChatSurface])

  const openPrivateChat = useCallback(async (targetUid: string, displayName: string) => {
    if (!currentUser) throw new Error('Sign in to open a private chat.')
    if (targetUid === currentUser.uid) throw new Error('Cannot open a private chat with yourself.')
    if (!canCreateDm || !createChannel || !setActiveChannelId) {
      throw new Error('Direct messages are turned off for your account.')
    }
    // Look for an existing 2-member private channel that pairs the current
    // user and the target. Channels are already streamed into channelsList
    // via ChatContext, so this is a client-side lookup — no extra reads.
    const existing = channelsList.find((channel) => {
      if (channel.type !== 'private') return false
      const roleUids = Object.keys(channel.roles ?? {})
      return roleUids.length === 2
        && roleUids.includes(currentUser.uid)
        && roleUids.includes(targetUid)
    })
    let channelId: string
    if (existing) {
      channelId = existing.id
    } else {
      const safeName = (displayName || 'Member').replace(/[^A-Za-z0-9 ]+/g, '').trim().slice(0, 40) || 'Member'
      // Add the current uid tail so slug collisions are astronomically unlikely.
      const tail = currentUser.uid.slice(0, 4)
      channelId = await createChannel({
        name: `DM · ${safeName} · ${tail}`,
        type: 'private',
        initialMemberIds: [targetUid],
      })
    }
    setActiveChannelId(channelId)
    openChatSurface()
  }, [canCreateDm, channelsList, createChannel, currentUser, openChatSurface, setActiveChannelId])

  const canPing = Boolean(currentUser?.uid && activeWorkspaceId)
  const canOpenPrivate = Boolean(currentUser?.uid && activeWorkspaceId && chat) && canCreateDm

  return { pingInWorkspaceChat, openPrivateChat, canPing, canOpenPrivate }
}
